<template>
  <DropdownGptSharingItem
    v-if="isFoundever"
    :key="selectedOption.label"
    :label="selectedOption.label"
    :description="selectedOption.description"
    :icon="selectedOption.icon"
    :isTopbarDisplay="true"
    class="DropdownGptSharing__active-item"
    @click.prevent
  />
  <UIDropdown
    v-else
    :items="filteredOptions"
    class="DropdownGptSharing"
    contentClass="DropdownGptSharing__content"
    :offset="[0, 0]"
    :width="'417px'"
    :maxWidth="'417px'"
    v-bind="$attrs"
    @onSelect="openConfirmationModal"
    @click.prevent
  >
    <template #toggle>
      <UIDropdownToggle class="DropdownGptSharing__trigger">
        <DropdownGptSharingItem
          v-if="selectedOption"
          :key="selectedOption.label"
          :label="selectedOption.label"
          :description="selectedOption.description"
          :icon="selectedOption.icon"
          :isTopbarDisplay="true"
          class="DropdownGptSharing__active-item"
        />
      </UIDropdownToggle>
    </template>

    <template #option="{ option, hide }">
      <DropdownGptSharingItem
        class="DropdownGptSharing__list-item"
        :label="option.label"
        :description="option.description"
        :icon="option.icon"
        :isSelected="selectedOption.label === option.label"
        @click.prevent="hide()"
      />
    </template>
  </UIDropdown>

  <ModalConfirm
    :isOpen="modalConfirmation.isOpen"
    confirmButtonLabel="Remove"
    @close="modalConfirmation.close()"
    @confirm="removePublicSharing"
  >
    <template #title> Remove sharing with everyone? </template>
    <template #content>
      You’re about to remove sharing this GPT with everyone and restrict access
      to selected users only.
    </template>
  </ModalConfirm>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { useModal } from '@/composables/useModal'
import UIDropdown from '@/views/components/ui/dropdowns/UIDropdown.vue'
import UIDropdownToggle from '@/views/components/ui/dropdowns/UIDropdownToggle.vue'

import DropdownGptSharingItem from './DropdownGptSharingItem.vue'
import ModalConfirm from '../modals/ModalConfirm.vue'

import type { IIcons } from '../ui/icons/types'

defineOptions({
  inheritAttrs: false,
})
const emit = defineEmits(['update:modelValue'])
const props = defineProps<{
  modelValue: boolean
  isFoundever?: boolean
}>()

interface IFilteredOption {
  label: string
  description: string
  icon: IIcons
}

const filteredOptions = computed<IFilteredOption[]>(() => [
  {
    label: 'Everyone',
    description: 'Anyone has access to this GPT.',
    icon: 'globe' as IIcons,
  },
  {
    label: 'Private',
    description: 'Only you or specific people can view this GPT.',
    icon: 'lock' as IIcons,
  },
])

const selectedOption = ref<IFilteredOption>(
  filteredOptions.value[filteredOptions.value.length - 1],
)
const setGptSharing = (option: IFilteredOption) => {
  const isPublic = option.label === 'Everyone'
  if (props.modelValue === isPublic) return

  selectedOption.value = option
  emit('update:modelValue', isPublic)
}

const modalConfirmation = useModal<IFilteredOption>()
const openConfirmationModal = (option: IFilteredOption) => {
  if (props.modelValue && option.label === 'Private') {
    modalConfirmation.open(option)
  } else {
    setGptSharing(option)
  }
}

const removePublicSharing = () => {
  if (modalConfirmation.modalData) {
    setGptSharing(modalConfirmation.modalData)
  }
  modalConfirmation.close()
}

watch(
  () => props.modelValue,
  (value) => {
    const selected = filteredOptions.value.find((option) =>
      value ? option.label === 'Everyone' : option.label === 'Private',
    )

    if (selected) {
      selectedOption.value = selected
    }
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
.DropdownGptSharing {
  .DropdownGptSharing__trigger {
    border-radius: 4px;
    width: 100%;

    &:hover {
      background-color: $color-neutral-5;
    }
  }

  .DropdownGptSharing__active-item {
    padding: 0.5rem;
    align-items: center;
  }
}
</style>

<style lang="scss">
.DropdownGptSharing__header {
  display: flex;
  gap: 0.25rem;
  align-items: center;
  padding: 0.75rem 0.75rem 0.75rem 0.75rem;
}

.DropdownGptSharing__header-label {
  @include font-size-12px;
  color: $color-neutral-150;
  font-weight: 700;
}
</style>
